import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";
import { WordReveal } from "@/components/Fx";
import { ITALY, MOROCCO } from "@/lib/mapPaths";

const STAR =
  "0,-1 0.247,-0.34 0.951,-0.309 0.399,0.13 0.588,0.809 0,0.42 -0.588,0.809 -0.399,0.13 -0.951,-0.309 -0.247,-0.34";

const LEGEND = {
  it: ["Rete in Italia", "Rete in Marocco"],
  en: ["Network in Italy", "Network in Morocco"],
  fr: ["Réseau en Italie", "Réseau au Maroc"],
};

function fitTransform(bounds, bx, by, bw, bh) {
  const [x0, y0, x1, y1] = bounds;
  const w = x1 - x0;
  const h = y1 - y0;
  const s = Math.min(bw / w, bh / h);
  const tx = bx + (bw - w * s) / 2 - x0 * s;
  const ty = by + (bh - h * s) / 2 - y0 * s;
  const drawnW = w * s;
  const drawnH = h * s;
  return { transform: `translate(${tx.toFixed(1)} ${ty.toFixed(1)}) scale(${s.toFixed(4)})`, tx, ty, drawnW, drawnH };
}

const itFit = fitTransform(ITALY.bounds, 430, 40, 340, 490);
const maFit = fitTransform(MOROCCO.bounds, 40, 230, 280, 310);

const NODE_IT = {
  x: itFit.tx + itFit.drawnW * 0.42,
  y: itFit.ty + itFit.drawnH * 0.32,
};
const NODE_MA = {
  x: maFit.tx + maFit.drawnW * 0.6,
  y: maFit.ty + maFit.drawnH * 0.24,
};
const TRAJECTORY = `M ${NODE_IT.x.toFixed(0)},${NODE_IT.y.toFixed(0)} C ${(NODE_IT.x - 40).toFixed(0)},${(NODE_IT.y - 150).toFixed(0)} ${(NODE_MA.x + 30).toFixed(0)},${(NODE_MA.y - 190).toFixed(0)} ${NODE_MA.x.toFixed(0)},${NODE_MA.y.toFixed(0)}`;

function CountryMap({ data, fit, fill, stroke }) {
  return (
    <g transform={fit.transform} fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinejoin="round">
      {data.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </g>
  );
}

function Node({ node, color }) {
  return (
    <g>
      <circle cx={node.x} cy={node.y} r="6" fill={color} />
      <circle cx={node.x} cy={node.y} r="6" fill="none" stroke={color} strokeWidth="2">
        <animate attributeName="r" values="6;20" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;0" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <polygon
        points={STAR}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinejoin="round"
        transform={`translate(${node.x} ${node.y - 16}) scale(1.1)`}
        className="twinkle"
      />
    </g>
  );
}

export default function CorridorMap() {
  const { lang } = useLang();
  const [legendIta, legendMar] = LEGEND[lang];
  return (
    <section className="bg-night" data-testid="corridor-map-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              La mappa
            </p>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-sand sm:text-4xl">
              <WordReveal text="Due paesi, un solo corridoio." delay={0.1} />
            </h2>
            <p className="mt-6 text-base leading-relaxed text-sand/65">
              La rete in Italia incontra la rete in Marocco: un corridoio unico per relazioni, investimenti e progetti.
            </p>
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3" data-testid="corridor-legend-ita">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-pine" />
                <span className="text-sm font-semibold text-sand/80">{legendIta}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="corridor-legend-mar">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-terra" />
                <span className="text-sm font-semibold text-sand/80">{legendMar}</span>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#131316]">
              <svg
                viewBox="0 0 800 560"
                className="h-auto w-full"
                data-testid="corridor-map-svg"
                role="img"
                aria-label="Mappa stilizzata del corridoio Italia–Marocco"
              >
                <defs>
                  <pattern id="seadots" width="26" height="26" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1.2" fill="#232327" />
                  </pattern>
                  <path id="corridorPath" d={TRAJECTORY} />
                </defs>

                <rect width="800" height="560" fill="url(#seadots)" />

                <CountryMap data={ITALY} fit={itFit} fill="#222228" stroke="#4A4A54" />
                <CountryMap data={MOROCCO} fit={maFit} fill="#222228" stroke="#4A4A54" />

                {/* country labels */}
                <text
                  x="390"
                  y="150"
                  textAnchor="middle"
                  fill="#5A5A66"
                  fontSize="30"
                  fontFamily="'Playfair Display', serif"
                  fontStyle="italic"
                  letterSpacing="4"
                >
                  Italia
                </text>
                <text
                  x="168"
                  y="205"
                  textAnchor="middle"
                  fill="#5A5A66"
                  fontSize="30"
                  fontFamily="'Playfair Display', serif"
                  fontStyle="italic"
                  letterSpacing="4"
                >
                  Marocco
                </text>

                {/* trajectory */}
                <path
                  d={TRAJECTORY}
                  fill="none"
                  stroke="#4A4A50"
                  strokeWidth="2.5"
                  strokeDasharray="1 11"
                  strokeLinecap="round"
                  className="dash-flow"
                />

                {/* endpoints */}
                <Node node={NODE_IT} color="#22A05C" />
                <Node node={NODE_MA} color="#D80000" />

                {/* comets travelling along the corridor */}
                <polygon
                  points={STAR}
                  fill="none"
                  stroke="#D80000"
                  strokeWidth="4"
                  strokeLinejoin="round"
                  transform="scale(1.5)"
                >
                  <animateMotion dur="7s" repeatCount="indefinite" rotate="auto">
                    <mpath href="#corridorPath" />
                  </animateMotion>
                </polygon>
                <polygon
                  points={STAR}
                  fill="none"
                  stroke="#22A05C"
                  strokeWidth="5"
                  strokeLinejoin="round"
                  transform="scale(1.1)"
                >
                  <animateMotion dur="9s" repeatCount="indefinite" rotate="auto" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
                    <mpath href="#corridorPath" />
                  </animateMotion>
                </polygon>
              </svg>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
